import { useFeatureFlag } from '@artic/ui/feature-flag';
import { createFileRoute } from '@tanstack/react-router';

import { FactoryAdminView } from '../../features/factory-admin/factory-admin-view';
import { FACTORY_ADMIN_KEY, type FactoryListItem } from '../../features/factory-admin/queries';
import { usePartnerId } from '../../features/inbox/use-partner-name';

export const Route = createFileRoute('/factories/')({
  staticData: {
    // Trailing crumb = the selected factory tab. Selection lives in the
    // `?factory=<uuid>` search param (nuqs, through the TanStack Router adapter),
    // so we read it off the match and resolve the name from the factory-list
    // cache. Falls back to the first non-archived factory (what the view shows
    // before a tab is chosen). Returns null while the list is still loading.
    breadcrumb: (match, queryClient) => {
      const lists = queryClient.getQueriesData<FactoryListItem[]>({
        queryKey: [FACTORY_ADMIN_KEY, 'list'],
      });
      const factories = lists.flatMap(([, data]) => data ?? []);
      const selectedId = (match.search as { factory?: string }).factory;
      const selected = selectedId ? factories.find((f) => f.factory_id === selectedId) : undefined;
      const factory = selected ?? factories.find((f) => !f.is_archived) ?? factories[0];
      if (!factory) return null;
      return factory.factory_name ?? '(unnamed)';
    },
  },
  component: FactoryAdminPage,
});

function FactoryAdminPage() {
  const enabled = useFeatureFlag('settings_factory');
  const partnerId = usePartnerId();

  if (!enabled) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Feature not available</h2>
        <p className="text-muted-foreground text-sm">
          Factory administration in Quality is not enabled for your organisation.
        </p>
      </div>
    );
  }

  return <FactoryAdminView partnerId={partnerId} />;
}
